// Pure helpers for the commission-pending-stale issue's decide flow
// (Baker 2026-07-30: "I don't know how to review the commission to see
// if it is accurate"). The issue's subject is a commission_reports row
// — an "activity" ref with no record page — so clicking its name
// dead-ended at the drawer's "no record page" copy, and the
// recommendation ("Review & process in the commission flow") named a
// destination the screen never linked to. UX rule #5: every action a
// user can't take here must offer a path.
//
// These helpers give every commission issue that path: the Commissions
// admin flow (/admin/commissions), which stays the system of record
// for the amounts — the list + per-disbursement worksheet (Calculate /
// Approve / Mark Paid) is where accuracy review actually happens; the
// send-to-advisor step lives on Baker's dashboard Commission Queue.
//
// Root-safe (imports nothing) so it's unit-tested from tests/web/ —
// same convention as effective-date-issue.ts / record-links.ts.

// Minimal structural subset of CleanupIssueSummary this module reads.
export interface CommissionIssueLike {
  detectorSlug: string;
  recommendation?: {
    params?: Record<string, unknown>;
  } | null;
  flagReasons?: unknown[];
}

export function isCommissionIssue(issue: CommissionIssueLike): boolean {
  return issue.detectorSlug === "commission-pending-stale";
}

// The five client-facing company slugs (CLAUDE.md data model). Local
// list, same convention as household-page-helpers.ts — used only to
// keep junk out of the URL, never to reject a link entirely.
const COMPANY_SLUGS = new Set([
  "mortgage",
  "insurance",
  "planning",
  "realty",
  "financial",
]);

// The report's client-facing company. The issue's owningCompanySlug is
// deliberately "productions" (back-office routing — see the detector's
// ROUTING block), so the company comes from recommendation.params
// (stamped by the detector), falling back to the "Company: <slug>"
// flag reason for issues materialized before the detector stamped
// params. Null when neither yields a known slug — the link then goes
// to the unfiltered flow rather than not existing (no dead end).
export function commissionIssueCompany(
  issue: CommissionIssueLike,
): string | null {
  const fromParams = issue.recommendation?.params?.["company"];
  if (typeof fromParams === "string") {
    const slug = fromParams.trim().toLowerCase();
    if (COMPANY_SLUGS.has(slug)) return slug;
  }
  for (const reason of issue.flagReasons ?? []) {
    if (typeof reason !== "string") continue;
    const m = /^company:\s*(\S+)/i.exec(reason.trim());
    if (m && m[1]) {
      const slug = m[1].toLowerCase();
      if (COMPANY_SLUGS.has(slug)) return slug;
    }
  }
  return null;
}

// Where "review & process" actually happens. Scoped to the report's
// company when we know it (default-to-your-desk: Baker lands on the
// insurance disbursements for an insurance report), unfiltered
// otherwise — always a real destination for a commission issue.
export function commissionFlowPath(issue: CommissionIssueLike): string {
  const company = commissionIssueCompany(issue);
  return company
    ? `/admin/commissions?company=${encodeURIComponent(company)}`
    : "/admin/commissions";
}

export const COMMISSION_FLOW_LINK_LABEL = "Open the commission flow";

// Honest Apply label (UX rule #6/#7 cousins: the button says what it
// does). The generic label read "Apply — Review & process in the
// commission flow", which sounded like clicking would OPEN the flow —
// it only marks the issue verified/resolved. — Baker 2026-07-30
export const COMMISSION_APPLY_LABEL =
  "Processed in the commission flow — mark resolved";
